import { formatDateISOUTC } from '~/services/TimeService';

import type { BrandSchedule, CampaignScheduleParentRow, CampaignSchedulePreview } from './types';

function isSameDate(a?: string | null, b?: string | null) {
  return !!a && !!b && formatDateISOUTC(a) === formatDateISOUTC(b);
}

export function buildCampaignScheduleParentRows(
  preview: CampaignSchedulePreview | undefined,
): Array<CampaignScheduleParentRow> {
  if (!preview) {
    return [];
  }

  const rows: Array<CampaignScheduleParentRow> = [];

  if (preview.availability) {
    rows.push({
      id: 'availability',
      type: 'availability',
      start: preview.availability.start,
      end: preview.availability.end,
      brands: preview.availability.brands,
      markets: preview.availability.markets,
      campaignTypes: preview.availability.campaignTypes,
    });
  }

  if (preview.online_purchase) {
    rows.push({
      id: 'online_purchase',
      type: 'online_purchase',
      start: preview.online_purchase.start,
      end: preview.online_purchase.end,
      brands: preview.online_purchase.brands,
      markets: preview.online_purchase.markets,
      campaignTypes: preview.online_purchase.campaignTypes,
    });
  }

  if (preview.lux_plus) {
    rows.push({
      id: 'lux_plus',
      type: 'lux_plus',
      start: preview.lux_plus.start,
      end: preview.lux_plus.end,
      brands: preview.lux_plus.brands,
      markets: preview.lux_plus.markets,
      campaignTypes: preview.lux_plus.campaignTypes,
    });
  }

  preview.list_visibility.forEach((row, index) => {
    rows.push({
      id: `list_visibility-${index}`,
      type: 'list_visibility',
      start: row.start,
      end: row.end,
      brand: row.brand,
      region: row.region,
      brands: row.brands,
      markets: row.markets,
      campaignTypes: row.campaignTypes,
    });
  });

  return rows;
}

export function getMatchingBrandSchedules(
  parentRow: CampaignScheduleParentRow,
  brandSchedules: Array<BrandSchedule>,
): Array<BrandSchedule> {
  return brandSchedules.filter((schedule) => {
    if (schedule.type !== parentRow.type) {
      return false;
    }

    if (
      parentRow.type === 'list_visibility' &&
      (schedule.brand !== parentRow.brand || schedule.region !== parentRow.region)
    ) {
      return false;
    }

    return isSameDate(schedule.start, parentRow.start) && isSameDate(schedule.end, parentRow.end);
  });
}

export function getMatchingBrandSchedulesByParentRow(
  parentRows: Array<CampaignScheduleParentRow>,
  brandSchedules: Array<BrandSchedule>,
): Map<string, Array<BrandSchedule>> {
  return new Map(parentRows.map((parentRow) => [parentRow.id, getMatchingBrandSchedules(parentRow, brandSchedules)]));
}

export function getUnmatchedBrandSchedules(
  matchingByParentRowId: Map<string, Array<BrandSchedule>>,
  brandSchedules: Array<BrandSchedule>,
): Array<BrandSchedule> {
  const matchedIds = new Set<number>();

  matchingByParentRowId.forEach((schedules) => {
    schedules.forEach((schedule) => matchedIds.add(schedule.id));
  });

  return brandSchedules.filter((schedule) => !matchedIds.has(schedule.id));
}
